import { MOTION } from '@/components/common/motion';
import { useToolSystem } from '@/context/ToolSystemContext';
import { TOOL_SYSTEMS, type ToolSystem, toolSystemLabelKey } from '@/types/toolSystem.types';
import { Wrench } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

/** One segment. The active one is filled, so the choice survives being read without colour. */
const segmentClasses = (active: boolean) =>
  `rounded-md px-2 py-1 text-sm font-medium ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
    active ? 'bg-primary text-white' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
  }`;

/**
 * Picks the tool system the whole app works in.
 *
 * A segmented control rather than a dropdown: there are two, the choice changes what every
 * list on the page shows, and that is worth being able to read at a glance.
 */
export const ToolSystemSwitcher: FC = () => {
  const { t } = useTranslation();
  const { toolSystem, setToolSystem } = useToolSystem();

  return (
    <div
      className="flex h-8 items-center gap-1 rounded-lg border border-gray-200 bg-white px-1"
      title={t('toolSystem.hint')}
    >
      <Wrench size={14} className="ml-1 shrink-0 text-gray-400" aria-hidden="true" />
      {TOOL_SYSTEMS.map((system: ToolSystem) => (
        <button
          key={system}
          type="button"
          onClick={() => setToolSystem(system)}
          aria-pressed={toolSystem === system}
          className={segmentClasses(toolSystem === system)}
        >
          {t(toolSystemLabelKey(system))}
        </button>
      ))}
    </div>
  );
};
